import { Component, Input, ViewChild, ElementRef, Renderer2, HostListener, NgZone, OnChanges, SimpleChange, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { animationFrameScheduler, fromEvent, merge, Observable, Subject } from 'rxjs';
import { auditTime, filter, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { Util } from '@libs/utilities/util';
import { Validator } from '@libs/utilities/validator';

import { INgImageComparisonConfig, NG_DEFAULT_IMAGE_COMPARISON_CONFIG, NgImageComparisonSchema } from './image-comparison.interface';


@Component({
    selector: 'ng-image-comparison',
    templateUrl: './image-comparison.component.html',
    styleUrls: ['./image-comparison.component.scss'],
    standalone: false
})
export class NgImageComparisonComponent implements OnInit, OnChanges, OnDestroy {

	@Output() readonly rendered: EventEmitter<void> = new EventEmitter<void>();

	//** Input decorators */
	@Input() config 	  : Partial<INgImageComparisonConfig> = {};
	@Input() preImageUrl  : string = '';
	@Input() postImageUrl : string = '';

	@Input() globalStyle   : Record<string, string> = {};
	@Input() preImageStyle : Record<string, string> = {};
	@Input() postImageStyle: Record<string, string> = {};

	@ViewChild('slider', 	{ static: false }) slider	 : ElementRef<HTMLDivElement>;
	@ViewChild('preImg', 	{ static: false }) preImage  : ElementRef<HTMLImageElement>;
	@ViewChild('postImg', 	{ static: false }) postImage : ElementRef<HTMLImageElement>;
	@ViewChild('resizer', 	{ static: true }) resizer	 : ElementRef<HTMLDivElement>;
	@ViewChild('grip', 		{ static: true }) gripLine	 : ElementRef<HTMLSpanElement>;

	sliderConfig		  : INgImageComparisonConfig;
	originalImage		  : string;
	currentImage		  : string;

	sliderActive$	 	  : Observable<MouseEvent | TouchEvent>;
	renderComparison$ 	  : Subject<void> = new Subject<void>();

	componentHeight		  : number = 0;
	componentWidth		  : number = 0;
	sliderHeight		  : number = 0;

	preImageLoaded		  : boolean = false;
	postImageLoaded		  : boolean = false;

	private readonly destroy$: Subject<void> = new Subject<void>();
	private isDragStreamSetup: boolean = false;

	constructor(
		private renderer: Renderer2,
		private ngZone  : NgZone
	) {}


	/**------------------------------------------------------
	 * Lifecycle Hooks
	 */
	ngOnInit(): void {

		//0 - check if the images are loaded
		this.renderComparison$.pipe(
			filter(() => this.preImageLoaded && this.postImageLoaded))
			.subscribe(() => {
				this.renderSliderComparison();
				this.rendered.emit();
			});

		//1 - Prepare and validate the config
		this.prepareAndValidateConfig();
	}

	ngOnChanges(changes: { config: SimpleChange; preImageUrl: SimpleChange; postImageUrl: SimpleChange }): void {
		if (changes.config || changes.preImageUrl || changes.postImageUrl) this.prepareAndValidateConfig();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}


	/**------------------------------------------------------
	 * Config Validation
	 */
	private prepareAndValidateConfig(): void {

		//0 - Validate the config
		this.validatePreImageUrl();
		this.validatePostImageUrl();

		//1 - Prepare the config
		const defaultConfig	: INgImageComparisonConfig = Util.Basic.deepCopy<INgImageComparisonConfig>(NG_DEFAULT_IMAGE_COMPARISON_CONFIG);
		const configWithUrls: INgImageComparisonConfig = Util.Basic.deepAssignPartial<INgImageComparisonConfig>(defaultConfig, { preImageUrl: this.preImageUrl, postImageUrl: this.postImageUrl });
		const sliderConfig	: INgImageComparisonConfig = Util.Basic.deepAssignPartial<INgImageComparisonConfig>(configWithUrls, this.config);

		//2 - Validate the config
		const errorMessages	: string[] = Validator.Schema.validationMessages(sliderConfig, NgImageComparisonSchema);
		if (Util.Array.isNotEmpty(errorMessages)) throw new Error(`NgImageComparisonComponent: ${errorMessages.join('\n')}`);

		//3 - Persist the config
		this.sliderConfig = sliderConfig;

		//4 - Updated the slider images
		this.renderComparison$.next();
	}

	private renderSliderComparison(): void {
		//0 - size the comparison from the latest/processed image. The original
		// image may have a different aspect ratio after an upscale, so allowing
		// it to establish the frame can make a portrait result render as square.
		const latestImageWidth : number = this.postImage.nativeElement.naturalWidth;
		const latestImageHeight: number = this.postImage.nativeElement.naturalHeight;
		if (latestImageWidth > 0 && latestImageHeight > 0) {
			this.renderer.setStyle(this.slider.nativeElement, '--comparison-image-aspect-ratio', `${latestImageWidth} / ${latestImageHeight}`);
		}

		//1 - Set the initial size of the slider
		this.renderer.setStyle(this.gripLine.nativeElement, 'width', `${this.sliderConfig.gripLineWidth}px`);
		this.renderer.setStyle(this.gripLine.nativeElement, 'margin-left', `${-this.sliderConfig.gripLineWidth / 2}px`);
		this.renderer.setStyle(this.gripLine.nativeElement, 'background', this.sliderConfig.gripLineColor);
		this.renderer.setStyle(this.gripLine.nativeElement, 'border', `${this.sliderConfig.gripRingWidth}px`);
		this.renderer.setStyle(this.gripLine.nativeElement, 'border-color', this.sliderConfig.gripRingColor);

		//2 - Setting the initial size of the postImage
		this.adjustImageSizeToComponent();
		this.setupDraggingEventStream();

		//3 - Setting the slider at the correct start position
		this.slide(this.sliderConfig.sliderPosPct);
	}

	private validatePreImageUrl(): void {

		//0 - Check if the preImageUrl is set
		if (Util.String.isEmpty(this.config.preImageUrl) && Util.String.isEmpty(this.preImageUrl)) {
			throw new Error('NgImageComparisonComponent: preImageUrl is a required input');
		}

		//1 - Check if the preImageUrl is set in both the config and as an input
		if (Util.String.isNotEmpty(this.config.preImageUrl) && Util.String.isNotEmpty(this.preImageUrl)) {
			throw new Error('NgImageComparisonComponent: preImageUrl cannot be set both in the config and as an input');
		}

		//2 - Set the original image
		this.originalImage = Util.String.isNotEmpty(this.config.preImageUrl) ? this.config.preImageUrl! : this.preImageUrl;
	}

	private validatePostImageUrl(): void {

		//0 - Check if the postImageUrl is set
		if (Util.String.isEmpty(this.config.postImageUrl) && Util.String.isEmpty(this.postImageUrl)) {
			throw new Error('NgImageComparisonComponent: postImageUrl is a required input');
		}

		//1 - Check if the postImageUrl is set in both the config and as an input
		if (Util.String.isNotEmpty(this.config.postImageUrl) && Util.String.isNotEmpty(this.postImageUrl)) {
			throw new Error('NgImageComparisonComponent: postImageUrl cannot be set both in the config and as an input');
		}

		//2 - Set the current image
		this.currentImage = Util.String.isNotEmpty(this.config.postImageUrl) ? this.config.postImageUrl! : this.postImageUrl;
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	@HostListener('window:resize', ['$event.target'])
	onResize(): void {
		this.adjustImageSizeToComponent();
		this.slide(this.sliderConfig.sliderPosPct);
	}

	private setupDraggingEventStream(): void {
		if (this.isDragStreamSetup) return;
		this.isDragStreamSetup = true;

		//0 - Setting up 'down events'
		const mouseDown$: Observable<MouseEvent> = fromEvent<MouseEvent>(this.gripLine.nativeElement, 'mousedown');
		const touchDown$: Observable<TouchEvent> = fromEvent<TouchEvent>(this.gripLine.nativeElement, 'touchstart');
		const down$		: Observable<MouseEvent | TouchEvent> = merge(mouseDown$, touchDown$).pipe(
			tap((event: MouseEvent | TouchEvent) => {
				event.stopPropagation(); // Prevent dragging the images (not sure if this is appropriate or even works)
				this.renderer.addClass(this.gripLine.nativeElement, 'draggable');
				this.renderer.addClass(this.resizer.nativeElement, 'resizable');
			})
		);

		//1 - Setting up 'up events'
		const mouseUp$: Observable<MouseEvent> = fromEvent<MouseEvent>(window, 'mouseup');
		const touchUp$: Observable<TouchEvent> = fromEvent<TouchEvent>(window, 'touchstop');
		const touchEnd$: Observable<TouchEvent> = fromEvent<TouchEvent>(window, 'touchend');
		const touchCancel$: Observable<TouchEvent> = fromEvent<TouchEvent>(window, 'touchcancel');
		const up$: Observable<MouseEvent | TouchEvent> = merge(mouseUp$, touchUp$, touchEnd$, touchCancel$).pipe(
			tap((event: MouseEvent | TouchEvent) => {
				event.preventDefault();
				this.renderer.removeClass(this.gripLine.nativeElement, 'draggable');
				this.renderer.removeClass(this.resizer.nativeElement, 'resizable');
			})
		);

		//2 - Setting up 'move events'
		const mouseMove$: Observable<MouseEvent> = fromEvent<MouseEvent>(window, 'mousemove');
		const touchMove$: Observable<TouchEvent> = fromEvent<TouchEvent>(window, 'touchmove');
		const move$: Observable<MouseEvent | TouchEvent> = merge(mouseMove$, touchMove$).pipe(tap((event: MouseEvent | TouchEvent) => { event.preventDefault(); }));

		//3 - Setting up the sliderActive$ stream
		this.ngZone.runOutsideAngular(() => {
			this.sliderActive$ = down$.pipe(
				mergeMap(() => move$.pipe(takeUntil(up$))),
				auditTime(0, animationFrameScheduler),
				takeUntil(this.destroy$)
			);
			this.sliderActive$.subscribe((movement: MouseEvent | TouchEvent) => {

				//a. Get the cursor's x position
				let gripPosition: number = this.getCursorPos(movement);

				//b. Prevent the slider from being positioned outside the image
				if (gripPosition < 0) gripPosition = 0;
				if (gripPosition > this.componentWidth) gripPosition = this.componentWidth;

				//c. Persisting position as pct to recalculate position during resize
				this.sliderConfig.sliderPosPct = (gripPosition / this.componentWidth) * 100;

				//d. Reveal the comparison at most once per animation frame
				this.slide(this.sliderConfig.sliderPosPct);
			});
		});
	}

	private adjustImageSizeToComponent(): void {

		//0 - Persist slider component width (after view init)
		this.componentWidth = this.slider.nativeElement.offsetWidth;

		//1 - Set width of postImage to same size as slider component
		this.renderer.setStyle(this.postImage.nativeElement, 'width', `${this.componentWidth}px`);
	}

	private getCursorPos(event: MouseEvent | TouchEvent): number {
		//0 - Attempt to stop dragging images. But seems not to work
		event.stopPropagation();

		//1 - Get the rect position of the image
		const elementRect: DOMRect = this.postImage.nativeElement.getBoundingClientRect();

		//2 - Get the right value for curser position. Different for touch and mouse
		let cursorX: number = 0;
		if (event instanceof MouseEvent) cursorX = event.clientX;
		else cursorX = event.touches[0].clientX;

		//3 - Calculate the cursor's x coordinate, relative to the image
		const imgRelativePosX: number = cursorX - elementRect.left;

		//4 - Return the cursor's x coordinate
		return imgRelativePosX;
	}

	private slide(sliderPositionPct: number): void {

		//0 - Recalculate position from pct to px
		const resizePosition: number = this.componentWidth * (sliderPositionPct / 100);

		//1 - Clip the processed image without triggering layout on every move
		this.renderer.setStyle(this.resizer.nativeElement, 'clip-path', `inset(0 ${100 - sliderPositionPct}% 0 0)`);

		//2 - Position the grip on the compositor layer
		this.renderer.setStyle(this.gripLine.nativeElement, 'transform', `translate3d(${resizePosition - this.sliderConfig.gripLineWidth / 4}px, 0, 0)`);
	}


	/**------------------------------------------------------
	 * Image Load Events
	 */
	onPreImageLoad(): void {
		this.preImageLoaded = true;
		this.renderComparison$.next();
	}

	onPreImageError(): void {
		throw new Error(`NgImageComparisonComponent => onPreImageError => FATAL ERROR: could not load image from url: ${this.originalImage}`);
	}

	onPostImageLoad(): void {
		this.postImageLoaded = true;
		this.renderComparison$.next();
	}

	onPostImageError(): void {
		throw new Error(`NgImageComparisonComponent => onPostImageError => FATAL ERROR: could not load image from url: ${this.currentImage}`);
	}
}
